import request from '@/utils/request'

/**
 * 图片上传
 * @param {File} file - 要上传的图片文件
 * @param {Object} options - 上传选项
 * @param {string} options.type - 图片类型 (avatar-头像, product-商品图片, license-营业执照, idcard-身份证, other-其他)
 * @param {string} options.module - 所属模块 (user-用户, store-门店, product-商品, other-其他)
 * @returns {Promise}
 */
export function uploadImage(file, options = {}) {
  const formData = new FormData()
  formData.append('file', file)
  
  if (options.type) {
    formData.append('type', options.type)
  }
  
  if (options.module) {
    formData.append('module', options.module)
  }
  
  return request({
    url: '/file/upload/image',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 文件上传
 * @param {File} file - 要上传的文件
 * @param {Object} options - 上传选项
 * @param {string} options.type - 文件类型 (contract-合同, certificate-证书, other-其他)
 * @param {string} options.module - 所属模块 (store-门店, admin-管理, other-其他)
 * @returns {Promise}
 */
export function uploadDocument(file, options = {}) {
  const formData = new FormData()
  formData.append('file', file)
  
  if (options.type) {
    formData.append('type', options.type)
  }
  
  if (options.module) {
    formData.append('module', options.module)
  }
  
  return request({
    url: '/file/upload/document',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 多文件上传
 * @param {FileList|Array} files - 要上传的多个文件
 * @param {Object} options - 上传选项
 * @param {string} options.type - 文件类型
 * @param {string} options.module - 所属模块
 * @returns {Promise}
 */
export function uploadMultipleFiles(files, options = {}) {
  const formData = new FormData()
  
  // 处理文件列表
  if (files instanceof FileList) {
    Array.from(files).forEach(file => {
      formData.append('files', file)
    })
  } else if (Array.isArray(files)) {
    files.forEach(file => {
      formData.append('files', file)
    })
  }
  
  if (options.type) {
    formData.append('type', options.type)
  }
  
  if (options.module) {
    formData.append('module', options.module)
  }
  
  return request({
    url: '/file/upload/multiple',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 删除文件
 * @param {string} fileId - 文件ID或文件名
 * @returns {Promise}
 */
export function deleteFile(fileId) {
  return request({
    url: `/file/${fileId}`,
    method: 'delete'
  })
}

/**
 * 获取文件列表
 * @param {Object} params - 查询参数
 * @param {string} params.module - 所属模块
 * @param {string} params.type - 文件类型
 * @param {number} params.page - 页码
 * @param {number} params.pageSize - 每页记录数
 * @returns {Promise}
 */
export function getFileList(params) {
  return request({
    url: '/file/list',
    method: 'get',
    params
  })
}

/**
 * 获取文件信息
 * @param {string} fileId - 文件ID或文件名
 * @returns {Promise}
 */
export function getFileInfo(fileId) {
  return request({
    url: `/file/${fileId}/info`,
    method: 'get'
  })
}

/**
 * 验证文件类型
 * @param {File} file - 文件对象
 * @param {Array} allowedTypes - 允许的文件类型
 * @returns {boolean}
 */
export function validateFileType(file, allowedTypes) {
  if (!file || !file.type) return false
  return allowedTypes.includes(file.type)
}

/**
 * 验证文件大小
 * @param {File} file - 文件对象
 * @param {number} maxSize - 最大大小（字节）
 * @returns {boolean}
 */
export function validateFileSize(file, maxSize) {
  if (!file) return false
  return file.size <= maxSize
}

/**
 * 格式化文件大小
 * @param {number} size - 文件大小（字节）
 * @returns {string}
 */
export function formatFileSize(size) {
  if (size === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(size) / Math.log(k))
  
  return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * 常用文件类型常量
 */
export const FILE_TYPES = {
  // 图片类型
  IMAGES: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  // 文档类型
  DOCUMENTS: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain'
  ]
}

/**
 * 文件大小限制常量（字节）
 */
export const FILE_SIZE_LIMITS = {
  IMAGE: 5 * 1024 * 1024,      // 5MB
  DOCUMENT: 10 * 1024 * 1024,  // 10MB
  TOTAL: 20 * 1024 * 1024      // 20MB
}

 